import { forwardRef, useMemo } from "react";

import { Box, BoxProps, Flex, Grid } from "@chakra-ui/react";
import { FormattedMessage } from "react-intl";
import { useShallow } from "zustand/react/shallow";

import { useLayoutStore } from "app/stores/useLayoutStore";
import { EppoFeatureFlags } from "app/types/flags";
import { PriorityIndicator } from "flows/Inventory/flows/RestockingList/components/RestockingListCard";
import { TranslatedRestockingItem } from "flows/Inventory/flows/RestockingList/models/restockingItem/types";
import { useRestockingServiceStore } from "flows/Inventory/flows/RestockingList/stores/restockingServiceStore";
import { useEppoFeatureFlagProvider } from "shared/hooks/useEppoFeatureFlag";
import { Button } from "ui/Button/Button";
import { InboundFruitOrVeggyChips } from "ui/InboundFruitOrVeggyChips/InboundFruitOrVeggyChips";
import { ProductFacing } from "ui/ProductFacing/ProductFacing";
import { ProductInformation } from "ui/ProductInformation/ProductInformation";
import { Shelf } from "ui/Shelf/Shelf";
import { Status } from "ui/Status/Status";
import { StockPill } from "ui/StockPill/StockPill";
import { isNotNullNorUndefined } from "utils/tsHelpers";

export type PrivateRestockingListItemCardTranslatedLabelsProps = {
  bioLabel: string;
  nonBioLabel: string;
  inStockLabel: string;
  backOnShelfLabel: string;
};

export type PrivateRestockingListItemCardProps = {
  restockingItem: TranslatedRestockingItem;
  isRestockingComplete: boolean;
  isBlocked: boolean;
  isClickable: boolean;
  isLoading?: boolean;
  onClickCard: () => void;
  onAdjustStockClick?: () => void;
} & PrivateRestockingListItemCardTranslatedLabelsProps &
  BoxProps;

export const PrivateRestockingListItemCard = forwardRef<
  HTMLDivElement,
  PrivateRestockingListItemCardProps
>(
  (
    {
      restockingItem: { product, sku, priorityTier = null },
      isRestockingComplete,
      isBlocked,
      isClickable,
      isLoading = false,
      onClickCard,
      bioLabel,
      nonBioLabel,
      inStockLabel,
      backOnShelfLabel,
      onAdjustStockClick,
      ...rest
    },
    ref,
  ) => {
    const {
      sku: productSku,
      name,
      imageUrl,
      shelfLetter,
      shelfNumber,
      countryOfOriginName,
      isBio,
      stockOnShelf,
      numberOfShelfFacings,
      numberOfShelfStackings,
      isShelvedInHandlingUnits,
    } = product ?? {};
    const isFruitOrVeggy = isNotNullNorUndefined(countryOfOriginName);
    const { isFeatureEnabled: isRestockingV2Enabled } = useEppoFeatureFlagProvider(
      EppoFeatureFlags.ENABLE_RESTOCKING_V2,
    );

    const { setUseRestockingServiceStore } = useRestockingServiceStore(
      useShallow((state) => ({
        setUseRestockingServiceStore: state.setUseRestockingServiceStore,
      })),
    );

    const { pageName } = useLayoutStore(
      useShallow((state) => {
        return {
          pageName: state.pageName,
        };
      }),
    );

    const displayedShelf =
      isNotNullNorUndefined(shelfLetter) && isNotNullNorUndefined(shelfNumber) ? (
        <Shelf gridColumnStart="3" letter={shelfLetter} number={shelfNumber} />
      ) : (
        <Status bg="grey.200" textColor="black" gridColumnStart="3" label="N/A" />
      );

    const displayedStatus = useMemo(() => {
      if (isRestockingComplete) {
        return <Status bg="veggie.100" textColor="black" label={backOnShelfLabel} />;
      }
      if (isFruitOrVeggy) {
        return (
          <InboundFruitOrVeggyChips
            bioLabel={bioLabel}
            nonBioLabel={nonBioLabel}
            countryOfOriginName={countryOfOriginName}
            isBio={!!isBio}
          />
        );
      }

      if (isClickable) {
        return (
          <Flex alignItems="center" gap="s100">
            {isRestockingV2Enabled && (
              <PriorityIndicator priorityTier={priorityTier} size="small" />
            )}
            <ProductFacing
              isShelvedInHandlingUnits={!!isShelvedInHandlingUnits}
              numberOfFacings={numberOfShelfFacings ?? 0}
              numberOfStacking={numberOfShelfStackings}
            />
          </Flex>
        );
      }

      return null;
    }, [
      isRestockingComplete,
      backOnShelfLabel,
      isFruitOrVeggy,
      bioLabel,
      nonBioLabel,
      isBio,
      countryOfOriginName,
      isShelvedInHandlingUnits,
      numberOfShelfFacings,
      numberOfShelfStackings,
      isClickable,
      isRestockingV2Enabled,
      priorityTier,
    ]);

    const goToStockCorrection = (e: React.MouseEvent) => {
      e.stopPropagation();
      setUseRestockingServiceStore({
        isNavigatingToStockCorrection: true,
        productSku,
        activeItemId: productSku,
        pageName,
      });
      onAdjustStockClick?.();
    };

    const bottomLeftElement = useMemo(() => {
      if (isClickable) return null;

      return (
        <Flex mt="s25" py="6px" justifyContent="end">
          <StockPill stock={stockOnShelf} customBgColor="grey.100" />
        </Flex>
      );
    }, [isClickable, stockOnShelf]);

    return (
      <Box
        onClick={isClickable && !isLoading && !isRestockingComplete ? onClickCard : () => {}}
        p="s200"
        bg="white"
        w="100%"
        filter={isBlocked ? "grayscale(100%)" : undefined}
        ref={ref}
        data-testid="restocking-list-item-card"
        {...rest}
      >
        <Grid mb="s200" templateColumns="auto 1fr min-content">
          {displayedStatus}
          {displayedShelf}
        </Grid>
        <ProductInformation
          bg="grey.100"
          name={name ?? ""}
          sku={sku || productSku}
          imageUrl={imageUrl ?? ""}
          showLeftSideBar={isRestockingComplete}
          leftSideBarProps={{ bgColor: "veggie.500" }}
          showSKU={!isClickable}
          stock={isClickable ? stockOnShelf : undefined}
          variantSize="sm"
        />
        {bottomLeftElement}
        <Button onClick={goToStockCorrection} variant="secondary" marginY="s100" isFullWidth>
          <FormattedMessage id="pages.restockingList.privateRestockingListItemCard.cta-button" />
        </Button>
      </Box>
    );
  },
);
